import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { expenseService, staffService, Expense, Staff } from '@/lib/storage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  MapPin,
  Plus,
  Loader2,
  CheckCircle2,
  Bike,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';

export default function StaffTravelAllowancePage() {
  const { session } = useAuth();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [staffProfile, setStaffProfile] = useState<Staff | null>(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    amount: '',
    remark: '',
    km: '',
  });

  useEffect(() => {
    loadExpenses();
  }, []);

  const loadExpenses = async () => {
    if (!session?.userId) return;
    try {
      const [expData, profileData] = await Promise.all([
        expenseService.getAll({ staffId: session.userId }),
        staffService.getById(session.userId),
      ]);
      setExpenses(expData);
      setStaffProfile(profileData || null);
    } catch (error) {
      console.error('Error loading expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const perKmRate = staffProfile?.bikeRatePerKm || 0;

  const handleKmChange = (val: string) => {
    const kmVal = val;
    if (kmVal && !isNaN(parseFloat(kmVal)) && parseFloat(kmVal) > 0) {
      const calc = perKmRate > 0 ? (parseFloat(kmVal) * perKmRate).toFixed(2) : '';
      setFormData(prev => ({
        ...prev,
        km: kmVal,
        amount: calc
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        km: kmVal,
        amount: ''
      }));
    }
  };

  const handleSubmit = async () => {
    if (!session?.userId) return;
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast({ title: 'Please enter a valid amount', variant: 'destructive' });
      return;
    }

    let finalRemark = formData.remark || 'Travel Allowance';
    if (formData.km && parseFloat(formData.km) > 0) {
      const kmInfo = `${formData.km} KM @ ₹${perKmRate}/km`;
      if (!finalRemark.includes(kmInfo)) {
        finalRemark = `${finalRemark} (${kmInfo})`;
      }
    }

    setSubmitting(true);
    try {
      const newExpense = await expenseService.create({
        staffId: session.userId,
        staffName: session.name,
        date: formData.date,
        amount: parseFloat(formData.amount),
        remark: finalRemark,
        category: 'Travel Allowance',
      });
      // Auto-approve for special staff
      if (newExpense?.id && newExpense.status === 'pending') {
        await expenseService.updateStatus(newExpense.id, 'approved', 'Auto-approved: Travel Allowance');
      }
      toast({ title: 'Travel allowance added and approved' });
      setDialogOpen(false);
      setFormData({ date: new Date().toISOString().split('T')[0], amount: '', remark: '', km: '' });
      await loadExpenses();
    } catch (error) {
      toast({ title: 'Failed to submit', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const approvedTotal = expenses.filter(e => e.status === 'approved').reduce((sum, e) => sum + e.amount, 0);
  const pendingTotal = expenses.filter(e => e.status === 'pending').reduce((sum, e) => sum + e.amount, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-purple-500 shrink-0" />
            Travel Allowance
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-0.5 sm:mt-1">Submit and track your travel allowance requests</p>
        </div>
        <Button onClick={() => { setFormData({ date: new Date().toISOString().split('T')[0], amount: '', remark: '', km: '' }); setDialogOpen(true); }} className="w-full sm:w-auto bg-purple-600 hover:bg-purple-700 text-white shrink-0">
          <Plus className="w-4 h-4 mr-1" /> Add Travel
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:gap-4">
        <Card className="bg-white/50 dark:bg-slate-900/40 border-emerald-200/50 dark:border-emerald-800/30">
          <CardContent className="p-3 sm:p-4 flex flex-col sm:flex-row items-start sm:items-center gap-1.5 sm:gap-3 min-w-0">
            <div className="p-1.5 sm:p-2 rounded-xl bg-emerald-500/10 shrink-0">
              <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm sm:text-2xl font-black text-emerald-600 dark:text-emerald-400 truncate">₹{approvedTotal.toLocaleString('en-IN')}</p>
              <p className="text-[9px] sm:text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide truncate">Approved</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white/50 dark:bg-slate-900/40 border-amber-200/50 dark:border-amber-800/30">
          <CardContent className="p-3 sm:p-4 flex flex-col sm:flex-row items-start sm:items-center gap-1.5 sm:gap-3 min-w-0">
            <div className="p-1.5 sm:p-2 rounded-xl bg-amber-500/10 shrink-0">
              <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm sm:text-2xl font-black text-amber-600 dark:text-amber-400 truncate">₹{pendingTotal.toLocaleString('en-IN')}</p>
              <p className="text-[9px] sm:text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide truncate">Pending</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white/50 dark:bg-slate-900/40 overflow-hidden">
        <CardContent className="p-0 overflow-x-auto">
          {expenses.length === 0 ? (
            <div className="text-center py-12">
              <MapPin className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
              <p className="text-sm font-medium text-slate-500 dark:text-slate-400">No travel allowance requests yet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Remark</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expenses.map((exp) => (
                  <TableRow key={exp.id}>
                    <TableCell className="font-medium">
                      {new Date(exp.date + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </TableCell>
                    <TableCell className="font-bold">₹{exp.amount.toLocaleString('en-IN')}</TableCell>
                    <TableCell className="max-w-[200px] truncate text-slate-500 dark:text-slate-400">{exp.remark || '-'}</TableCell>
                    <TableCell>
                      <span className={cn(
                        'px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wide',
                        exp.status === 'approved' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' :
                        exp.status === 'pending' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                        'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400'
                      )}>
                        {exp.status}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Travel Allowance</DialogTitle>
            <DialogDescription>Submit a travel allowance request for admin review.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">Date</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="h-10.5 rounded-xl border-slate-200 dark:border-slate-800"
              />
            </div>

            <div className="p-3.5 rounded-2xl bg-purple-50/60 dark:bg-purple-950/20 border border-purple-200/50 dark:border-purple-800/30 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-purple-700 dark:text-purple-300 flex items-center gap-1.5">
                  <Bike className="w-4 h-4 text-purple-600" />
                  Travel Allowance Options
                </span>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300">
                  Rate: ₹{perKmRate}/km
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-[11px] font-bold text-slate-600 dark:text-slate-400">Kilometers (KM)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    placeholder="e.g. 15"
                    value={formData.km}
                    onChange={(e) => handleKmChange(e.target.value)}
                    className="h-9 rounded-xl border-purple-200 dark:border-purple-900/50 bg-white dark:bg-slate-900"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-[11px] font-bold text-slate-600 dark:text-slate-400">Amount (₹)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    disabled={!!formData.km && parseFloat(formData.km) > 0}
                    className="h-9 rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 disabled:opacity-60 disabled:bg-slate-100 dark:disabled:bg-slate-800"
                  />
                </div>
              </div>

              {formData.km && parseFloat(formData.km) > 0 ? (
                <p className="text-[11px] font-medium text-purple-600 dark:text-purple-400 flex items-center gap-1">
                  ✓ {formData.km} KM × ₹{perKmRate}/km = ₹{formData.amount} (Amount section auto-calculated & disabled)
                </p>
              ) : (
                <p className="text-[10px] text-slate-500 dark:text-slate-400">
                  Fill KM to auto-calculate amount, or enter Amount directly for manual travel claim.
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">Remarks</Label>
              <Textarea
                placeholder="Travel details..."
                value={formData.remark}
                onChange={(e) => setFormData({ ...formData, remark: e.target.value })}
                rows={3}
                className="rounded-xl border-slate-200 dark:border-slate-800"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting} className="bg-purple-600 hover:bg-purple-700 text-white">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
